import React from 'react';
import type { PosProduct } from '../../posProduct';

type UseProductGridActivationParams = {
    /** A tile's own tap/press/Enter: adds a single-variant product, opens the picker for a several-variant one. */
    onSelectProduct: (product: PosProduct) => 'added' | 'variantPicker';
    /** An add by mouse or touch: the caller sends focus home. */
    onProductAddedByPointer: () => void;
    /** The grid's other capture-phase `keydown` handling (the printable-key redirect). */
    onKeyDownCapture: (event: React.KeyboardEvent) => void;
};

/**
 * Tells a tile activated by mouse or touch from one activated by keyboard
 * (bos-seller#55): a pointer add sends focus home, a keyboard add keeps focus
 * on the tile. The list box's `onAction` doesn't carry the interaction's
 * modality, so a `pointerdown` on the grid's wrapping element notes it in a
 * ref first, and the action reads and clears it. Every `keydown` clears it
 * too, before the grid's other key handling, so a stale flag from an earlier
 * pointer interaction never leaks into a later keyboard add: by the time a
 * keyboard `Enter` fires the action, its own `keydown` has cleared the ref.
 */
export const useProductGridActivation = ({
    onSelectProduct,
    onProductAddedByPointer,
    onKeyDownCapture,
}: UseProductGridActivationParams) => {
    const wasPointerRef = React.useRef(false);

    const onGridKeyDownCapture = (event: React.KeyboardEvent) => {
        wasPointerRef.current = false;
        onKeyDownCapture(event);
    };

    const onGridPointerDownCapture = () => {
        wasPointerRef.current = true;
    };

    const onProductAction = (product: PosProduct) => {
        const wasPointer = wasPointerRef.current;
        wasPointerRef.current = false;
        const outcome = onSelectProduct(product);
        if (outcome === 'added' && wasPointer) {
            onProductAddedByPointer();
        }
    };

    return { onGridKeyDownCapture, onGridPointerDownCapture, onProductAction };
};
